import React from 'react';
import { insertWordBreaks } from '@/_utils/utils';
import { ClickCopy } from '@/exports/columnCommonComponents';
import styled from '@emotion/styled';
import { Box, Button, Divider, Grid, IconButton, Popover, Typography } from '@mui/material';
import PolarisIcon from '@/components/_common/PolarisIcon';

interface StatusReasonPopoverProps {
  statusReason: string | undefined | null;
  onTaskClick?: () => void;
  isFailed: boolean;
  children?: React.ReactNode;
  statusReasonAppend?: React.ReactNode;
}

const StatusReasonPopover = ({ statusReason, onTaskClick, isFailed, statusReasonAppend, children }: StatusReasonPopoverProps) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (e: any) => {
    setAnchorEl(e.currentTarget);
  };

  const id = open ? 'status-reason-popover' : undefined;

  const handleClose = () => {
    setAnchorEl(null);
  };

  if (!statusReason && children) return <div>{children}</div>;
  return (
    <div onClick={(e) => e.stopPropagation()}>
      {children ? (
        <Button data-testid="popover-trigger" variant="text" color="default" onClick={handleClick} sx={{ px: 1, ml: -1 }}>
          {children}
        </Button>
      ) : (
        <StatusReasonButton
          aria-describedby={id}
          onClick={handleClick}
          color="warning"
          variant="outlined"
          size="small"
          endIcon={<PolarisIcon icon="popup" height={13} />}
        >
          Details
        </StatusReasonButton>
      )}

      <StyledPopover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        disableScrollLock
      >
        <Grid container justifyContent="space-between">
          <HeaderTypography variant="h4">Details</HeaderTypography>
          <CloseButton onClick={handleClose} size="small">
            <PolarisIcon icon="close" height={16} />
          </CloseButton>
        </Grid>
        <Divider sx={{ borderColor: 'warning.main', borderStyle: 'dotted', mb: 0.5 }} />
        {statusReason?.length && (
          <Box sx={{ my: 2, px: 1.5 }}>
            <ClickCopy copyText={statusReason} placement="left">
              <BodyTypography variant="body1">{insertWordBreaks(statusReason) || '(none)'}</BodyTypography>
            </ClickCopy>
          </Box>
        )}
        {statusReasonAppend && statusReasonAppend}
        <Grid container justifyContent="flex-end">
          {isFailed && !!onTaskClick && (
            <ViewTaskButton size="small" variant="text" color="warning" onClick={onTaskClick} sx={{ mt: -1 }}>
              View Task
            </ViewTaskButton>
          )}
        </Grid>
      </StyledPopover>
    </div>
  );
};

export default StatusReasonPopover;

const StatusReasonButton = styled(Button)`
  padding: 0 ${({ theme }) => theme.spacing(0.75)};
  color: ${({ theme }) => theme.palette.warning.main};
  border-color: ${({ theme }) => theme.palette.warning.main};
  opacity: 0.6;
  font-size: 0.8rem;
  border-style: dotted;
  transition: opacity 0.2s ease;
  border-radius: 3px;
  &:hover {
    opacity: 0.8;
    border-style: dotted;
    background: none;
    transition: opacity 0.2s ease;
  }
`;

const StyledPopover = styled(Popover)`
  overflow: visible !important;
  .MuiPopover-paper {
    margin-top: ${({ theme }) => theme.spacing(1)};
    border: 1px dotted ${({ theme }) => theme.palette.warning.main};
    width: 300px;
    padding: 0;
  }
`;

const HeaderTypography = styled(Typography)`
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  padding: ${({ theme }) => theme.spacing(1, 1.5)};
  color: ${({ theme }) => theme.palette.warning.main};
  font-size: 0.88rem;
`;

const BodyTypography = styled(Typography)`
  max-height: 100px;
  overflow-y: auto;
  overflow-x: hidden;
  font-size: 0.9rem;
  opacity: 0.8;
  white-space: pre-wrap;
`;

const CloseButton = styled(IconButton)`
  padding: ${({ theme }) => theme.spacing(0.25)};
  margin-right: ${({ theme }) => theme.spacing(0.5)};
  margin-left: auto;
  align-self: center;
  opacity: 0.33;
  transition: opacity 0.2s ease;
  &:hover {
    opacity: 0.66;
    transition: opacity 0.2s ease;
  }
`;

const ViewTaskButton = styled(Button)`
  margin-bottom: ${({ theme }) => theme.spacing(0.5)};
  font-size: 0.84rem;
  opacity: 0.8;
  transition: opacity 0.2s ease;
  &:hover {
    background: none;
    opacity: 1;
    transition: opacity 0.2s ease;
  }
`;
